import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { type IColumn, type IRowDefault } from 'shared/ui/TableNew/store/types'
import { EmptyCell, Icon, Status, toastStore, Tooltip, Typography } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { logger, prepareSearchPhoneNumber, truthy } from 'shared/lib'
import { type IResponseNumber } from 'entities/Phone/api/types'
import { InboxesApi } from 'entities/Inbox'
import { PhoneApi } from 'entities/Phone'
import { getNumberStatus } from 'entities/Phone/ui/NumbersStatus/config'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { RemoveNumberModalContent } from 'pages/settings/pages/inboxes/pages/id/pages/numbers/ui/RemoveNumberModal/RemoveNumberModalContent'
import { CallForwardingStore } from 'pages/settings/pages/inboxes/pages/id/pages/numbers/store/callForwardingStore'
import styles from './styles.module.scss'
import { type InboxLayerStore } from './inboxLayerStore'

export type INumbersRow = IRowDefault & {
  fields: IResponseNumber
}
export class NumbersStore {
  private _tableStore: TableStore<INumbersRow>
  private _numbers: IResponseNumber[] = []
  private _loading = false
  private _search = ''
  private _modalId = nanoid()
  private _selectedReassignNumber: IResponseNumber | null = null
  private _allAssignedNumbers: IResponseNumber[] = []
  private _loadingAssignedNumbers = false
  private _loadingRemoveNumber = false
  private _currentInboxId: number | null = null
  private _excludeNumberId: number | null = null
  private _numberSearch = ''
  private _callForwardingStore: CallForwardingStore

  constructor(readonly inboxLayerStore: InboxLayerStore, private _entities: IEntitiesStore) {
    this._callForwardingStore = new CallForwardingStore(this)
    makeAutoObservable(this)

    this._tableStore = new TableStore({
      key: 'inbox-numbers-table',
    })
  }

  get tableStore() {
    return this._tableStore
  }

  get loading() {
    return this._loading
  }

  get search() {
    return this._search
  }

  get numbers() {
    return this._numbers
  }

  get callForwardingStore() {
    return this._callForwardingStore
  }

  get currentInboxId() {
    return this._currentInboxId
  }

  get entities() {
    return this._entities
  }

  get noResultsFound() {
    return !this.numbersTableRows.length && !this.loading && this.search.trim() !== ''
  }

  get isAdminRole() {
    return this._entities.usersStore.user?.isAdminRole
  }

  get shouldShowAddNumbersFlow() {
    return this.isAdminRole && this._entities.userPermissionsStore.can_manage_billing_settings
  }

  get canShowCallForwarding() {
    const { subscriptionStore, billingStore, featureFlagsStore } = this._entities
    if (featureFlagsStore.settings_inboxes_manage_inbox_calling_v2) {
      return false
    }
    const isPAYG = subscriptionStore.isPAYGPlan
    const isTrial = subscriptionStore.isTrial

    if (isPAYG || isTrial) {
      return billingStore.autorecharge?.is_active
    }

    return this.inboxLayerStore.currentTeam?.number?.voice
  }

  get selectedReassignNumber() {
    return this._selectedReassignNumber
  }

  get loadingAssignedNumbers() {
    return this._loadingAssignedNumbers
  }

  get loadingRemoveNumber() {
    return this._loadingRemoveNumber
  }

  get numberSearch() {
    return this._numberSearch
  }

  setNumberSearch = (value: string) => {
    this._numberSearch = value
  }

  get availableNumbersForReassign(): IResponseNumber[] {
    if (!this._excludeNumberId) {
      return []
    }

    const availableNumbers = this._allAssignedNumbers.filter(
      (num) => num.id !== this._excludeNumberId
    )

    if (!this._numberSearch.trim()) {
      return availableNumbers
    }

    const searchTerm = prepareSearchPhoneNumber(this._numberSearch)

    return availableNumbers.filter((num) => {
      const cleanNumber = prepareSearchPhoneNumber(num.number)
      return cleanNumber.includes(searchTerm)
    })
  }

  get filteredNumbers() {
    if (!this._search.trim()) {
      return this.numbers
    }

    const term = this._search.toLowerCase()

    return this.numbers.filter((num) => {
      return num.number?.includes(term)
    })
  }

  get numbersTableRows(): INumbersRow[] {
    return this.filteredNumbers.map((num) => {
      return {
        id: String(num.id),
        expanded: false,
        is_can_expanded: false,
        isMoreExpand: false,
        loadingMore: false,
        loading: false,
        actionsProps: null,
        actions: [
          !num.is_aircall &&
            this.canShowCallForwarding && {
              icon: 'arrowRight',
              text: num.call_forwarding?.is_active ? 'Edit forwarding' : 'Call forwarding',
              onAction: () => this._callForwardingStore.openCallForwardingModal(num),
            },
          this.filteredNumbers.length > 1 && {
            icon: 'close',
            text: 'Remove number',
            onAction: () => this.openRemoveNumberModal(num),
          },
        ],
        isDisabled: false,
        isCheckboxDisabled: false,
        ariaLabel: `Number ${num.formatted_number || num.number}`,
        columnsKeys: ['number', 'callForwarding', 'status', 'type'],
        setColumnsKeys: () => {},
        fields: num,
        withActions: true,
        origin: num,
      }
    })
  }

  get getNumbersTableColumns(): IColumn<INumbersRow>[] {
    const columns: Array<IColumn<INumbersRow> | false> = [
      {
        field: 'number',
        name: 'Number',
        width: '195px',
        renderRowCell: (row) => {
          const num = row.fields
          const getLocation = () => {
            if (num.is_toll_free) {
              return undefined
            }
            let address = [num.city, num.state].filter(Boolean).join(', ')

            const area_code = num.area_code

            if (!address && area_code?.state_name && area_code?.state_code) {
              address = `${area_code.state_name}, ${area_code.state_code}`
            }
            return address
          }
          const location = getLocation()

          return (
            <div className={styles.nameInfoContainer}>
              <div className={styles.nameContainer}>
                <Typography
                  variant='body-md-medium'
                  className={styles.numberContainer}
                  ariaLabel='inbox-number-formatted'
                  width={'210px'}
                  ellipsis
                >
                  {num.formatted_number} {num.is_aircall && <Icon icon={'aircall'} />}
                </Typography>
                {location && (
                  <Typography
                    variant='body-sm-regular'
                    color='var(--content-primary-tertiary)'
                    ariaLabel='inbox-number-national'
                    width={'210px'}
                    ellipsis
                  >
                    {location}
                  </Typography>
                )}
              </div>
            </div>
          )
        },
      },
      !this.entities.featureFlagsStore.settings_inboxes_manage_inbox_calling_v2 && {
        field: 'callForwarding',
        name: 'Call forwarding',
        width: '260px',
        renderRowCell: (row) => {
          const cf = row.fields.call_forwarding
          if (cf?.is_active && cf?.formatted_number) {
            return (
              <div className={styles.row8}>
                <Typography
                  variant='body-md-regular'
                  ariaLabel='inbox-number-call-forwarding'
                  ellipsis
                >
                  {cf?.formatted_number}
                </Typography>
                {cf?.extension && (
                  <>
                    <Icon
                      icon={'ellipse_6'}
                      fontSize={2}
                      color={'var(--content-neutral-primary)'}
                    />
                    <Typography
                      variant='body-md-regular'
                      ariaLabel='point'
                      ellipsis
                      color={'tertiary'}
                    >
                      Ext: {cf?.extension}
                    </Typography>
                  </>
                )}
              </div>
            )
          }

          if (row.fields.is_aircall) {
            return (
              <Tooltip label='Call forwarding is unavailable through Aircall.' placement='top'>
                <Typography
                  variant='body-md-regular'
                  ariaLabel='inbox-number-call-forwarding'
                  color='var(--content-primary-disabled)'
                >
                  Unavailable
                </Typography>
              </Tooltip>
            )
          }

          return (
            <Typography
              variant='body-md-regular'
              ariaLabel='inbox-number-call-forwarding'
              color='var(--content-primary-disabled)'
            >
              Disabled
            </Typography>
          )
        },
      },
      {
        field: 'status',
        name: 'Status',
        width: '200px',
        renderRowCell: (row) => {
          const typesWithStatus = ['Landline', 'Toll-Free']

          const status = this.getStatus(row)
          const statusProps = getNumberStatus(status)
          const isShowStatus = typesWithStatus.includes(row.fields.type)

          if (!status || !statusProps || !isShowStatus) {
            return <EmptyCell />
          }

          return (
            <Typography variant='body-md-regular' ariaLabel='inbox-number-status' ellipsis>
              <Status
                title={statusProps.title}
                intent={statusProps.intent}
                icon={statusProps.icon}
                emphasis={statusProps.emphasis}
              />
            </Typography>
          )
        },
      },
      {
        field: 'type',
        name: 'Type',
        width: '160px',
        renderRowCell: (row) => {
          const type = row.fields.type
          return (
            <Typography variant='body-md-regular' ariaLabel='inbox-number-type' ellipsis>
              {type}
            </Typography>
          )
        },
      },
    ]
    return columns.filter(truthy)
  }

  init = async (inboxId: number) => {
    if (!inboxId) {
      return
    }

    runInAction(() => {
      this._loading = true
      this._currentInboxId = inboxId
    })

    try {
      const numbers = await InboxesApi.getInboxNumbers(inboxId)

      runInAction(() => {
        this._numbers = numbers.data
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  getStatus = (row: INumbersRow) => {
    const { type, verified_status } = row.fields
    if (type === 'Landline') {
      const status = row.fields.landline?.status?.toLowerCase()
      if (status === 'completed') {
        return 'verifiedLandline'
      }
      if (status === 'failed') {
        return 'failed'
      }
      return 'pending'
    }

    return verified_status?.toLowerCase()
  }

  setSearch = (search: string) => {
    this._search = search.replace(/\D/g, '')
  }

  dispose = () => {
    runInAction(() => {
      this._numbers = []
    })
    this._callForwardingStore.dispose()
  }

  fetchAllAssignedNumbers = async () => {
    runInAction(() => {
      this._loadingAssignedNumbers = true
    })

    try {
      const { data } = await PhoneApi.getNumbers({
        page: 1,
        limit: 500,
      })

      runInAction(() => {
        this._allAssignedNumbers = data.data
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loadingAssignedNumbers = false
      })
    }
  }

  setSelectedReassignNumber = (number: IResponseNumber | null) => {
    this._selectedReassignNumber = number
  }

  closeModal = () => {
    this.setSelectedReassignNumber(null)
    this._numberSearch = ''
    this._excludeNumberId = null
    modalStore.removeModal(this._modalId)
  }

  openRemoveNumberModal = async (number: IResponseNumber) => {
    this.fetchAllAssignedNumbers()
    runInAction(() => {
      this._excludeNumberId = number.id
    })

    modalStore.addModal({
      id: this._modalId,
      title: 'Remove number',
      width: 480,
      fullHeight: true,
      showCloseButton: false,
      paddingContent: '0 24px 24px',
      onClose: this.closeModal,
      ModalContentProps: {
        store: this,
        number,
      },
      ModalContent: RemoveNumberModalContent,
    })
  }

  removeNumber = async (numberId: number) => {
    if (!this.selectedReassignNumber) {
      return
    }
    try {
      runInAction(() => {
        this._loadingRemoveNumber = true
      })

      await PhoneApi.unassignNumberFromInbox(numberId, {
        reassign_number_id: this.selectedReassignNumber?.id,
      })
      this.closeModal()

      if (this._currentInboxId) {
        await this.inboxLayerStore.init(this._currentInboxId)
      }
      toastStore.add({
        type: 'success',
        title: 'Number removed',
      })
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        if (e.errors.getError('reassign_number_id')) {
          toastStore.add({
            type: 'error',
            title: e.errors.getError('reassign_number_id'),
          })
        }
      }
    } finally {
      runInAction(() => {
        this._loadingRemoveNumber = false
      })
    }
  }
}
